import { notifyError } from '../../utils/Notify';

/**
 * Configuration for Payzen chunk loading error handling
 */
const PAYZEN_ERROR_CONFIG = {
  MAX_CHUNK_ERRORS: 1,
  PAYZEN_CHUNK_ERROR_PATTERN: /kr-asset-.*\.bundle\.js|krypton-client.*\.js/i,
  PAYZEN_DOMAIN: 'static.payzen.eu',
};

/**
 * Checks if an error is related to Payzen chunk loading
 */
const isPayzenChunkError = (errorMessage, errorSource, errorStack = '') => {
  const errorString = `${errorMessage} ${errorSource} ${errorStack}`.toLowerCase();
  
  const isChunkLoadError = 
    errorMessage.includes('ChunkLoadError') || 
    errorMessage.includes('Loading chunk') ||
    errorMessage.includes('Failed to fetch dynamically imported module') ||
    errorString.includes('chunkloade') ||
    errorString.includes('loading chunk');

  // Check Payzen patterns in all sources
  const hasPayzenPattern = 
    PAYZEN_ERROR_CONFIG.PAYZEN_CHUNK_ERROR_PATTERN.test(errorSource) || 
    PAYZEN_ERROR_CONFIG.PAYZEN_CHUNK_ERROR_PATTERN.test(errorMessage) ||
    PAYZEN_ERROR_CONFIG.PAYZEN_CHUNK_ERROR_PATTERN.test(errorStack) ||
    errorString.includes(PAYZEN_ERROR_CONFIG.PAYZEN_DOMAIN) ||
    errorString.includes('kr-asset') ||
    errorString.includes('kr-asset-visa') ||
    errorString.includes('kr-asset-mastercard') ||
    errorString.includes('krypton-client');

  return (isChunkLoadError && hasPayzenPattern) || 
         (isChunkLoadError && errorString.includes('payzen')) ||
         (hasPayzenPattern && isChunkLoadError);
};

/**
 * Displays an error modal and reloads the page
 */
export const handlePaymentLoadFailure = () => {
  const title = 'Erreur Paiement';
  const description = 'Les options de paiement n\'ont pas pu être chargées. La page va être rechargée.';
  notifyError(title, description, () => {
    window.location.reload();
  });
};

/**
 * Creates a global error handler for Payzen chunk loading errors
 * @param {Function} onFailure - Function called when max number of errors is reached
 * @param {number} maxErrors - Maximum number of errors before calling onFailure
 * @returns {Function} Cleanup function to remove listeners
 */
export const setupPayzenChunkErrorListener = (onFailure = handlePaymentLoadFailure, maxErrors = PAYZEN_ERROR_CONFIG.MAX_CHUNK_ERRORS) => {
  let chunkErrorCount = 0;
  let isActive = true;
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;

  const checkAndHandleError = (errorMessage, errorSource = '', errorStack = '', errorObject = null) => {
    if (!isActive) return false;
    
    if (isPayzenChunkError(errorMessage, errorSource, errorStack)) {
      chunkErrorCount++;
      console.error(`[Payzen Error Handler] Chunk load error detected (${chunkErrorCount}/${maxErrors}):`, {
        message: errorMessage,
        source: errorSource,
        stack: errorStack,
        error: errorObject,
      });

      if (chunkErrorCount >= maxErrors) {
        console.error(`[Payzen Error Handler] Too many errors (${chunkErrorCount}). Triggering failure handler.`);
        isActive = false;
        cleanup();
        onFailure();
        return true;
      }
    }
    return false;
  };

  // Intercept console.error to capture Vue errors
  console.error = (...args) => {
    originalConsoleError.apply(console, args);
    
    if (!isActive) return;
    
    // Extract information from console.error arguments
    const errorString = args.map(arg => {
      if (typeof arg === 'string') return arg;
      if (arg instanceof Error) return `${arg.message} ${arg.stack}`;
      return String(arg);
    }).join(' ');
    
    const errorMessage = args.find(arg => typeof arg === 'string') || '';
    const errorObject = args.find(arg => arg instanceof Error);
    const errorStack = errorObject?.stack || errorString;
    
    // Check if it's a Payzen chunk error
    if (errorString.includes('ChunkLoadError') || errorString.includes('kr-asset') || errorString.includes('payzen')) {
      checkAndHandleError(errorMessage, '', errorStack, errorObject);
    }
  };

  // Intercept console.warn as well (Vue sometimes uses console.warn)
  console.warn = (...args) => {
    originalConsoleWarn.apply(console, args);
    
    if (!isActive) return;
    
    const errorString = args.map(arg => {
      if (typeof arg === 'string') return arg;
      if (arg instanceof Error) return `${arg.message} ${arg.stack}`;
      return String(arg);
    }).join(' ');
    
    if (errorString.includes('ChunkLoadError') || errorString.includes('kr-asset') || errorString.includes('payzen')) {
      const errorMessage = args.find(arg => typeof arg === 'string') || '';
      const errorObject = args.find(arg => arg instanceof Error);
      const errorStack = errorObject?.stack || errorString;
      checkAndHandleError(errorMessage, '', errorStack, errorObject);
    }
  };

  const handleGlobalError = (event) => {
    if (!isActive) return;
    
    const errorMessage = event.message || '';
    const errorSource = event.filename || event.target?.src || '';
    const errorStack = event.error?.stack || '';

    checkAndHandleError(errorMessage, errorSource, errorStack, event.error);
  };

  const handleUnhandledRejection = (event) => {
    if (!isActive) return;
    
    const error = event.reason;
    const errorMessage = error?.message || String(error) || '';
    const errorStack = error?.stack || '';
    
    checkAndHandleError(errorMessage, '', errorStack, error);
  };

  // Cleanup function
  const cleanup = () => {
    isActive = false;
    console.error = originalConsoleError;
    console.warn = originalConsoleWarn;
    window.removeEventListener('error', handleGlobalError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  };

  // Add listeners
  window.addEventListener('error', handleGlobalError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  // Return cleanup function
  return cleanup;
};

